import { DreamerPieChartDataType } from "@/components/DreamerCharts/types";
import { CHART_SEGMENT_COLORS } from "@/components/DreamerCharts/chartColors";
// Note: build-* helpers source colors only from the trusted rotation; the
// allowlist guard lives at the render boundary (segmentColorAt) where
// caller-supplied overrides are accepted.
import {
  DreamerChartAxisLabels,
  DreamerTableRow
} from "@/entities/attachments/types";
import { reducePrecision } from "@/utils/numbers";

interface BuildPieChartDataParams {
  rows: DreamerTableRow[];
  axisLabels: DreamerChartAxisLabels | null;
  formatLabel: (raw: unknown) => string;
}

export const buildPieChartData = ({
  rows,
  axisLabels,
  formatLabel
}: BuildPieChartDataParams): DreamerPieChartDataType[] => {
  if (!axisLabels?.x || !axisLabels?.y) return [];

  const xKey = axisLabels.x;
  const yKey = axisLabels.y;

  // Drop non-positive values: a pie can't represent zero or negative slices,
  // and including them either zeros out the chart or, with mixed signs,
  // produces percentages > 100 / < 0 that break Recharts' rendering.
  const slices = rows.flatMap((row) => {
    const raw = row[yKey];
    const numericValue = typeof raw === "number" ? raw : Number(raw);
    if (!Number.isFinite(numericValue) || numericValue <= 0) return [];
    return [
      {
        name: formatLabel(row[xKey]),
        raw: numericValue
      }
    ];
  });

  const total = slices.reduce((sum, slice) => sum + slice.raw, 0);
  if (total <= 0) return [];

  return slices.map((slice, index) => ({
    name: slice.name,
    value: reducePrecision((slice.raw / total) * 100, 1),
    color: CHART_SEGMENT_COLORS[index % CHART_SEGMENT_COLORS.length]
  }));
};
